import { useCallback, useRef, useState } from 'react'
import {
  dismissUpdate,
  fetchRemoteVersion,
  getLocalBuildId,
  getLocalBuiltAt,
  isUpdateAvailable,
  isUpdateDismissed,
  remoteUpdateKey,
} from '@/utils/appVersion'
import { useVersionPollEffects } from './useVersionPollEffects'

/** Polls `/version.json`; skipped on Vite dev server. */
export function useAppVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [remoteVersion, setRemoteVersion] = useState(null)
  const localRef = useRef({ buildId: getLocalBuildId(), builtAt: getLocalBuiltAt() })
  const checkingRef = useRef(false)

  const checkForUpdate = useCallback(async () => {
    if (import.meta.env.DEV || checkingRef.current) return
    checkingRef.current = true
    try {
      const remote = await fetchRemoteVersion()
      if (!remote?.buildId) return
      setRemoteVersion(remote)
      const key = remoteUpdateKey(remote)
      setUpdateAvailable(isUpdateAvailable(localRef.current, remote) && !isUpdateDismissed(key))
    } finally {
      checkingRef.current = false
    }
  }, [])

  useVersionPollEffects(checkForUpdate)

  const reloadToUpdate = useCallback(() => window.location.reload(), [])
  const dismiss = useCallback(() => {
    const key = remoteUpdateKey(remoteVersion)
    if (key) dismissUpdate(key)
    setUpdateAvailable(false)
  }, [remoteVersion])

  return { updateAvailable, reloadToUpdate, dismiss, checkForUpdate }
}
